import {ContentProvider, ContentProviderType} from "./content-provider";
import {
  Content,
  ContentAnnotation,
  ContentComplement,
  ContentDoubt, Question, QuestionStatus, ScormCheckpoint
} from "../../../infrastructure/presentation/views/content/helpers";
import {Rating, RatingList} from "../rating";
import {EventContentUseCase} from "../../usecase/content_provider/event/content_interface";
import {Instructor} from "../entities";

export class EventContentProvider implements ContentProvider {
  constructor(eventID: number, classID: number, contentUseCase: EventContentUseCase) {
    this._contentUseCase = contentUseCase;
    this._eventID = eventID;
    this._classID = classID;
  }

  private _contentUseCase: EventContentUseCase;
  private readonly _eventID: number;
  private readonly _classID: number;

  getID(): number {
    return this._eventID;
  }

  getClassID(): number {
    return this._classID;
  }

  getType(): ContentProviderType {
    return ContentProviderType.EVENT;
  }

  async getAllContents(): Promise<Content[]> {
    return await this._contentUseCase.getAllContents(this._eventID, this._classID);
  }

  async getContentByID(contentID: number): Promise<Content> {
    return await this._contentUseCase.getContentByID(this._eventID, this._classID, contentID);
  }

  async initializeContent(contentID: number): Promise<void> {
    return await this._contentUseCase.initializeContent(this._eventID, this._classID, contentID);
  }

  async getAllComplements(contentID: number): Promise<ContentComplement[]> {
    return await this._contentUseCase.getAllComplements(this._eventID, this._classID, contentID);
  }

  async getContentInstructors(contentID: number): Promise<Instructor[]> {
    return await this._contentUseCase.getContentInstructors(this._eventID, this._classID, contentID);
  }

  async createContentAnnotation(contentID: number, annotation: ContentAnnotation): Promise<ContentAnnotation> {
    return await this._contentUseCase.createContentAnnotation(this._eventID, this._classID, contentID, annotation);
  };

  async getAllContentAnnotations(contentID: number): Promise<ContentAnnotation[]> {
    return await this._contentUseCase.getAllContentAnnotations(this._eventID, this._classID, contentID);
  };

  async updateContentAnnotation(contentID: number, annotation: ContentAnnotation): Promise<ContentAnnotation> {
    return await this._contentUseCase.updateContentAnnotation(this._eventID, this._classID, contentID, annotation);
  };

  async deleteContentAnnotation(contentID: number, annotationID: number): Promise<void> {
    return await this._contentUseCase.deleteContentAnnotation(this._eventID, this._classID, contentID, annotationID);
  };

  async saveCheckpointSeconds(contentID: number, timestamp: number): Promise<void> {
    return await this._contentUseCase.saveCheckpointSeconds(this._eventID, this._classID, contentID, timestamp);
  };

  async saveCheckpointSCORM(contentID: number, scormCheckpoint: ScormCheckpoint): Promise<void> {
    return await this._contentUseCase.saveCheckpointSCORM(this._eventID, this._classID, contentID, scormCheckpoint);
  };

  async saveProgress(contentID: number, speedRate: number): Promise<number> {
    return await this._contentUseCase.saveProgress(this._eventID, this._classID, contentID, speedRate);
  };

  async generateQuestionnaire(contentID: number): Promise<number> {
    return await this._contentUseCase.generateQuestionnaire(this._eventID, this._classID, contentID);
  };

  async finishContent(contentID: number): Promise<void> {
    return await this._contentUseCase.finishContent(this._eventID, this._classID, contentID);
  };

  async getQuestionByPage(contentID: number, questionnaireID: number, page: number): Promise<Question> {
    return await this._contentUseCase.getQuestionByPage(this._eventID, this._classID, contentID, questionnaireID, page);
  };

  async replyQuestionByPage(contentID: number, questionnaireID: number, page: number, alternativeID: number): Promise<QuestionStatus> {
    return await this._contentUseCase.replyQuestionByPage(this._eventID, this._classID, contentID, questionnaireID, page, alternativeID);
  };

  async replyDescriptiveQuestionByPage(contentID: number, questionnaireID: number, page: number, answer: string): Promise<QuestionStatus> {
    return await this._contentUseCase.replyDescriptiveQuestionByPage(this._eventID, this._classID, contentID, questionnaireID, page, answer);
  }

  async finishContentProvider(): Promise<void> {
    // no need implementation
    return new Promise((resolve, reject) => resolve());
  };

  async createDoubt(contentID: number, contentDoubt: ContentDoubt): Promise<ContentDoubt> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  createRating(contentID: number, rating: Rating): Promise<Rating> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  getAllDoubts(contentID: number): Promise<ContentDoubt[]> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  getAllRatings(contentID: number, index?: number): Promise<RatingList> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  likeRating(ratingID: number): Promise<void> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  removeLikeRating(ratingID: number): Promise<void> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };

  deleteRating(ratingID: number, contentID: number): Promise<void> {
    // no need implementation
    return new Promise((resolve, reject) => reject("Not implemented"));
  };
}
